import { driveClient } from './driveClient';
import { entryIconKind } from './format';

/**
 * Thumbnail cache for the drive grid.
 *
 * The preview endpoint needs the session bearer, so an `<img src>` cannot fetch it directly;
 * a blob URL stands in. Images only — video/audio/pdf fall back to their type icons — and the
 * cache is a small LRU so a long session cannot accumulate hundreds of live object URLs.
 */

const MAX_CACHE = 120;
const MAX_BYTES = 10 * 1024 * 1024;

const cache = new Map<string, string>();
const pending = new Map<string, Promise<string | null>>();
const failed = new Set<string>();

function remember(id: string, url: string): string {
  cache.set(id, url);
  if (cache.size > MAX_CACHE) {
    const oldest = cache.keys().next().value;
    if (oldest && oldest !== id) {
      const evicted = cache.get(oldest);
      if (evicted) URL.revokeObjectURL(evicted);
      cache.delete(oldest);
    }
  }
  return url;
}

/** True when the entry is a small image the browser can preview cheaply. */
export function isThumbnailable(entry: { id: string; name: string; mimeType?: string; size: number; kind: string }): boolean {
  if (entry.kind !== 'file') return false;
  if (failed.has(entry.id)) return false;
  if (entry.size > MAX_BYTES) return false;
  return entryIconKind(entry.name, entry.mimeType) === 'image';
}

/** The cached object URL, or null while absent — callers re-render when the promise settles. */
export function cachedThumbnail(entryId: string): string | null {
  return cache.get(entryId) ?? null;
}

export async function loadThumbnail(entry: {
  id: string;
  name: string;
  mimeType?: string;
  size: number;
  kind: string;
}): Promise<string | null> {
  if (!isThumbnailable(entry)) return null;
  const existing = cache.get(entry.id);
  if (existing) return existing;
  const running = pending.get(entry.id);
  if (running) return running;
  const task = (async () => {
    try {
      const blob = await driveClient.readPreviewBlobById(entry.id);
      if (!blob.type.startsWith('image/')) {
        failed.add(entry.id);
        return null;
      }
      return remember(entry.id, URL.createObjectURL(blob));
    } catch {
      failed.add(entry.id);
      return null;
    } finally {
      pending.delete(entry.id);
    }
  })();
  pending.set(entry.id, task);
  return task;
}
